"use client"
import StarSvg from "src/components/svg/StarSvg";
import theme from "src/components/themes/DefaultTheme";
import Draft, { ContentState, EditorState } from 'draft-js';
import Immutable from 'immutable';
import type IMovieReview from "src/lib/filmwebid/IMovieReview";
import { type UserReviewsItem } from "src/lib/movieinfo/movieinfotypes";
import styled from 'src/lib/styles/css';
import { useMemo, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';


//#region [Props]
type ReviewProps = {
	review: IMovieReview | UserReviewsItem;
	className?: string;
	onTextClick?: () => void;
};
//#endregion

//#region [Component]
export default function Review({ review, className, onTextClick }: ReviewProps) {
	const [isExpanded, setIsExpanded] = useState(false);

	const editorState = useMemo(() => {
		let editorState = EditorState.createWithContent(ContentState.createFromText(review.reviewText || "", '\u0007'));
		return { truncated: truncate(editorState, 100), original: editorState };
	}, [review.reviewText]);


	return <SReview className={className} onClick={onTextClick}>
		{(review.rating ?? 0) > 0 && <SReviewRating>
			<SReviewRatingValueStar selected={review.rating! >= 1} />
			<SReviewRatingValueStar selected={review.rating! >= 2} />
			<SReviewRatingValueStar selected={review.rating! >= 3} />
			<SReviewRatingValueStar selected={review.rating! >= 4} />
			<SReviewRatingValueStar selected={review.rating! === 5} />
		</SReviewRating>}
		<SEditorContainer onClick={() => setIsExpanded(true)}>
			<Editor
				toolbarHidden
				readOnly
				//@ts-ignore
				blockRenderMap={extendedBlockRenderMap}
				editorState={isExpanded ? editorState.original : editorState.truncated}
				wrapperClassName="EditorWrapper"
				editorClassName="Editor"
				blockStyleFn={blockStyleFn}
				blockRendererFn={blockRendererFn}
			/>
		</SEditorContainer>
	</SReview>;

}
//#endregion



//#region [Component]
type TruncatedBlockProps = {
	block: Draft.ContentBlock;
	[key: string]: unknown;
};

function TruncatedBlock({ block }: TruncatedBlockProps) {
	return <div>
		{block.getText()}
		<SReadAll >Les mer</SReadAll>
	</div>
}
//#endregion

//#region [Other]
const TRUNCATED_BLOCK_TYPE = "truncated";

// Create a new block type
const blockRenderMap = Immutable.Map({
	TRUNCATED_BLOCK_TYPE: {
		element: 'div'
	}
});

//... add the new block type to the default list of block types
const extendedBlockRenderMap = Draft.DefaultDraftBlockRenderMap.merge(blockRenderMap);

// Add css classes to blocks
function blockStyleFn(contentBlock: Draft.ContentBlock): string | undefined {
	if (contentBlock.getType() === TRUNCATED_BLOCK_TYPE) {
		return "TruncatedBlock";
	}
}

function blockRendererFn(contentBlock: Draft.ContentBlock) {
	const type = contentBlock.getType();
	if (type === TRUNCATED_BLOCK_TYPE) {
		return {
			component: TruncatedBlock,
			editable: false
		};
	}
}

// https://github.com/facebook/draft-js/issues/742#issuecomment-270836953
function truncate(editorState: Draft.EditorState, charCount: number) {
	const contentState = editorState.getCurrentContent();
	const blocks = contentState.getBlockMap();

	let count = 0;
	let isTruncated = false;
	const truncatedBlocks: Draft.ContentBlock[] = [];
	blocks.forEach((block) => {
		if (!isTruncated) {
			const length = block!.getLength();
			if (count + length > charCount) {
				isTruncated = true;
				const truncatedText = block!.getText().slice(0, charCount - count);
				const state = ContentState.createFromText(`${truncatedText}...`);
				truncatedBlocks.push((state.getFirstBlock() as any).merge({ type: "truncated" }));
			} else {
				truncatedBlocks.push(block!);
			}
			count += length + 1;
		}
	});

	if (isTruncated) {
		const state = ContentState.createFromBlockArray(truncatedBlocks);
		return EditorState.createWithContent(state);
	}

	return editorState;
}
//#endregion

//#region [Styles]
const SReview = styled.div`
	font-size: ${theme.fonts.tittelXXS.size};
`;

const SReviewRating = styled.div`
	height: 24px;
	margin-bottom: 12px;
	display: flex;
	gap: 12px;
`;

const SReviewRatingValueStar = styled(StarSvg)`
	height: 24px;
	path {
		fill: ${theme.palette.turkis2};
	}
`;

const SReadAll = styled.span`
	text-decoration: underline;
	margin-left: 1ch;
`;

const SEditorContainer = styled.div`

	.EditorWrapper {
		div[data-block="true"]:first-child > div {
			margin-top: 0;
		}
		div[data-block="true"]:last-child > div {
			margin-bottom: 0;
		}
	}
	.Editor {
		.TruncatedBlock {
			cursor: pointer;
		}
	}

`
//#endregion